import React from 'react';
import {
  Button,
  Flex,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

import xhr from 'xhr';
import useAuth from 'hooks/useAuth';
import AiPagination from 'components/monitoring/ai/AiPagination';
import { BRAND, EmptyState, PageHeader, SectionCard } from '../components/ui';

const PAGE_SIZE = 20;

const fullName = (user) => `${user?.first_name || ''} ${user?.last_name || ''}`.trim();

// Estudiantes de la asesora con exactamente un pago registrado (mismo criterio de antes).
function NewsStudents() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);
  const { user } = useAuth();

  React.useEffect(() => {
    // Strapi entrega máximo 250 registros por consulta (config/api.js → maxLimit),
    // así que se recorren todas las páginas de pagos de los estudiantes de la asesora.
    const load = async () => {
      try {
        setLoading(true);
        const base =
          `/payments?filters[student][adviser][id][$eq]=${user.adviser.id}` +
          `&fields[0]=amount&fields[1]=method&fields[2]=payment_date&fields[3]=createdAt` +
          `&populate[student][populate]=user&sort[0]=createdAt:desc&pagination[pageSize]=250`;

        const first = await xhr(`${base}&pagination[page]=1`);
        const pageCount = first.data.meta?.pagination?.pageCount || 1;
        const rest = await Promise.all(
          Array.from({ length: pageCount - 1 }, (_, i) => xhr(`${base}&pagination[page]=${i + 2}`))
        );
        const payments = [first, ...rest].flatMap((res) => res.data.data);

        const byStudent = new Map();
        payments.forEach((payment) => {
          const student = payment.student;
          if (!student?.id) return;
          if (!byStudent.has(student.id)) byStudent.set(student.id, { student, payments: [] });
          byStudent.get(student.id).payments.push(payment);
        });

        setRows(
          [...byStudent.values()]
            .filter((el) => el.payments.length === 1)
            .map((el) => ({ ...el.student, payment: el.payments[0] }))
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        );
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  return (
    <>
      <PageHeader
        title={`Nuevos estudiantes${loading ? '' : ` (${rows.length})`}`}
        subtitle="Tus estudiantes que tienen un solo pago registrado."
      />

      <SectionCard padding={0} overflow="hidden">
        {loading ? (
          <Flex justify="center" py={12}>
            <Spinner color={BRAND} />
          </Flex>
        ) : rows.length === 0 ? (
          <EmptyState title="Aún no tienes estudiantes nuevos" description="Aquí aparecen cuando se registra su primer pago." />
        ) : (
          <TableContainer>
            <Table>
              <Thead bg="gray.50">
                <Tr>
                  <Th>Estudiante</Th>
                  <Th isNumeric>Monto</Th>
                  <Th>Método</Th>
                  <Th>Fecha de pago</Th>
                  <Th textAlign="right">Detalle</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((el) => (
                  <Tr key={el.id} _hover={{ bg: 'gray.50' }}>
                    <Td>
                      <Text fontWeight="600" color="gray.800">
                        {fullName(el.user) || 'Sin nombre'}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Código {el.id}
                      </Text>
                    </Td>
                    <Td isNumeric fontSize="sm">
                      {Number(el.payment.amount || 0).toLocaleString('es-CO')}
                    </Td>
                    <Td fontSize="sm">{el.payment.method}</Td>
                    <Td fontSize="sm">{dayjs(el.payment.payment_date || el.payment.createdAt).format('DD/MM/YYYY')}</Td>
                    <Td textAlign="right">
                      <Button
                        as={Link}
                        to={`/plataforma/asesoras/ver-estudiante/${el.id}`}
                        size="sm"
                        variant="outline"
                        colorScheme="purple"
                      >
                        Ver
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
        {!loading && (
          <AiPagination
            pagination={{ page, pageSize: PAGE_SIZE, total: rows.length, pageCount: Math.ceil(rows.length / PAGE_SIZE) }}
            noun="estudiantes"
            onPageChange={setPage}
          />
        )}
      </SectionCard>
    </>
  );
}

export default NewsStudents;
